import { ProfileGenTestCaseView } from "generated/graphql";
import { useSnackbar } from "notistack";
import { useAppDispatch } from "redux/hooks";
import {
  deleteProfileGenTestCase,
  updateProfileGenTestCase,
  validateProfileGenTestCase,
} from "redux/slices/lensEditor";
import { useTestJsonParser } from "./useTestJsonParser";

export function useTestCaseManager() {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const jsonParser = useTestJsonParser();

  async function validateTestCase(data: ProfileGenTestCaseView) {
    if (data.id === undefined) {
      return;
    }

    const parsed = jsonParser(data.expectedResponse);

    if (parsed === undefined) {
      return;
    }

    return dispatch(
      validateProfileGenTestCase({
        id: data.id,
        lensId: data.lensId,
        expectedResponse: JSON.stringify(parsed),
        expectFailure: data.expectFailure,
        description: data.description as string,
      })
    )
      .unwrap()
      .catch((err) => {
        enqueueSnackbar(err?.message ?? "Error", { variant: "error" });
      });
  }

  async function updateTestCase(data: ProfileGenTestCaseView) {
    if (data.id === undefined) {
      return;
    }

    const parsed = jsonParser(data.expectedResponse);

    if (parsed === undefined) {
      return;
    }

    return dispatch(
      updateProfileGenTestCase({
        id: data.id,
        lensId: data.lensId,
        fieldId: data.fieldId,
        expectedResponse: JSON.stringify(parsed),
        expectFailure: data.expectFailure,
        description: data.description as string,
      })
    )
      .unwrap()
      .then(() => {
        enqueueSnackbar("Test Case Updated", { variant: "info" });
      })
      .catch((err) => {
        enqueueSnackbar(err?.message ?? "Error", { variant: "error" });
      });
  }

  async function handleDelete({ id, lensId, fieldId }: ProfileGenTestCaseView) {
    dispatch(
      deleteProfileGenTestCase({
        testCaseId: id,
        lensId,
        fieldId,
      })
    )
      .unwrap()
      .then(() => {
        enqueueSnackbar("Deleted", { variant: "info" });
      })
      .catch((err) => {
        enqueueSnackbar(err?.message ?? "Error", { variant: "error" });
      });
  }

  return {
    validateTestCase,
    updateTestCase,
    handleDelete,
  };
}

export default useTestCaseManager;
